import { GetPayUpType, GetPayUpsResponseType, PutPayUpType } from '@orbit/api-types'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { nowUnix as currentUnix } from '@lib/datetime'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface UpdatePayUpVariables {
    tripId: string
    payUpId: number
    payUpData: PutPayUpType
    currentUserId: number
}

interface UpdatePayUpContext {
    snapshot: GetPayUpsResponseType | undefined
}

export const useUpdatePayUp = () => {
    const queryClient = useQueryClient()

    return useMutation<GetPayUpType, Error, UpdatePayUpVariables, UpdatePayUpContext>({
        mutationFn: async ({ tripId, payUpId, payUpData }) => {
            const response = await api.patch<GetPayUpType>(
                `/trips/${tripId}/payUps/${payUpId}`,
                payUpData,
            )
            return response.data
        },
        onMutate: async ({ tripId, payUpId, payUpData, currentUserId }) => {
            const queryKey = queryKeys.payups(tripId)
            await queryClient.cancelQueries({ queryKey })
            const snapshot = queryClient.getQueryData<GetPayUpsResponseType>(queryKey)
            const nowUnix = currentUnix()
            queryClient.setQueryData<GetPayUpsResponseType>(queryKey, prev => {
                if (!prev) return prev
                return {
                    payUps: prev.payUps.map(p =>
                        p.id === payUpId
                            ? {
                                  ...p,
                                  amount: payUpData.amount ?? p.amount,
                                  currencyCode: payUpData.currencyCode ?? p.currencyCode,
                                  lastUpdatedAt: nowUnix,
                                  lastUpdatedByUserId: currentUserId,
                              }
                            : p,
                    ),
                }
            })
            return { snapshot }
        },
        onError: (_err, variables, context) => {
            if (context) {
                queryClient.setQueryData(queryKeys.payups(variables.tripId), context.snapshot)
            }
        },
        onSettled: (_data, _err, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.payups(variables.tripId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.payup(variables.tripId, variables.payUpId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.paymentsOwed(variables.tripId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.expenses(variables.tripId) })
        },
    })
}
